/**
 * Holidays composable
 * Manages holiday calendar state and CRUD operations
 */
import { ref, computed, readonly } from 'vue'
import { collection, doc, getDocs, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { getFirebaseDb } from '@/core/firebase/firebase'

// Hebrew day names (starting from Sunday)
export const DAY_NAMES = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש']

// Hebrew month names
export const HEBREW_MONTHS = [
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
]

// Shared state
const holidays = ref({}) // { 'YYYY-MM-DD': 'event text' }
const loading = ref(false)
const currentMonth = ref(new Date().getMonth())
const currentYear = ref(new Date().getFullYear())

/**
 * Format date to YYYY-MM-DD string
 * @param {Date} date
 * @returns {string}
 */
export function formatDateKey(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Format date for display (DD.MM)
 * @param {Date} date
 * @returns {string}
 */
export function formatDateDisplay(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${day}.${month}`
}

/**
 * Composable for holiday calendar
 */
export function useHolidays() {
  const error = ref(null)
  const saving = ref(false)

  // Today's date key for comparison
  const todayKey = formatDateKey(new Date())

  // ============================================
  // Computed Properties
  // ============================================

  /**
   * Generate dates for current month
   */
  const monthDates = computed(() => {
    const dates = []
    const daysInMonth = new Date(currentYear.value, currentMonth.value + 1, 0).getDate()

    for (let day = 1; day <= daysInMonth; day++) {
      dates.push(new Date(currentYear.value, currentMonth.value, day))
    }
    return dates
  })

  /**
   * Organize days into weeks (including prev/next month padding)
   */
  const weeks = computed(() => {
    const weeksArray = []
    let week = []

    // Add days from previous month to fill first week
    const firstDayOfMonth = new Date(currentYear.value, currentMonth.value, 1)
    const offset = firstDayOfMonth.getDay()
    for (let i = offset - 1; i >= 0; i--) {
      const prevDate = new Date(currentYear.value, currentMonth.value, -i)
      week.push({ date: prevDate, isOtherMonth: true })
    }

    // Add days from current month
    monthDates.value.forEach((date) => {
      week.push({ date: date, isOtherMonth: false })
      if (week.length === 7) {
        weeksArray.push(week)
        week = []
      }
    })

    // Add days from next month to fill last week
    if (week.length > 0) {
      let nextDay = 1
      while (week.length < 7) {
        const nextDate = new Date(currentYear.value, currentMonth.value + 1, nextDay)
        week.push({ date: nextDate, isOtherMonth: true })
        nextDay++
      }
      weeksArray.push(week)
    }

    return weeksArray
  })

  /**
   * Get current week days (Sun-Thu for work week)
   */
  const currentWeekDays = computed(() => {
    const today = new Date()
    const dayOfWeek = today.getDay()

    // Find Sunday of current week
    const sunday = new Date(today)
    sunday.setDate(today.getDate() - dayOfWeek)

    // Generate Sun-Thu (5 days)
    const days = []
    for (let i = 0; i < 5; i++) {
      const date = new Date(sunday)
      date.setDate(sunday.getDate() + i)
      days.push({
        date,
        dateKey: formatDateKey(date),
        dayName: DAY_NAMES[i],
        isToday: formatDateKey(date) === todayKey,
      })
    }
    return days
  })

  /**
   * Current week events for widget
   */
  const currentWeekEvents = computed(() => {
    return currentWeekDays.value.map((day) => ({
      ...day,
      event: holidays.value[day.dateKey] || null,
    }))
  })

  /**
   * Check if current week has any events
   */
  const hasWeekEvents = computed(() => {
    return currentWeekEvents.value.some((day) => day.event)
  })

  /**
   * Current month/year display string
   */
  const monthYearDisplay = computed(() => {
    return `${HEBREW_MONTHS[currentMonth.value]} ${currentYear.value}`
  })

  // ============================================
  // Actions
  // ============================================

  /**
   * Load all holidays from Firestore
   */
  async function loadHolidays() {
    loading.value = true
    error.value = null

    try {
      const db = getFirebaseDb()
      const snapshot = await getDocs(collection(db, 'holiday'))

      const holidaysData = {}
      snapshot.docs.forEach((doc) => {
        const data = doc.data()
        if (data.date && data.text) {
          holidaysData[data.date] = data.text
        }
      })

      holidays.value = holidaysData
    } catch (err) {
      console.error('Error loading holidays:', err)
      error.value = 'לא ניתן לטעון את האירועים'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Save/update an event
   * @param {string} dateKey - Date in YYYY-MM-DD format
   * @param {string} text - Event text
   * @param {string} userId - Current user ID
   */
  async function saveEvent(dateKey, text, userId) {
    saving.value = true
    error.value = null

    try {
      const db = getFirebaseDb()
      await setDoc(doc(db, 'holiday', dateKey), {
        date: dateKey,
        text: text,
        updatedAt: serverTimestamp(),
        updatedBy: userId || '',
      })

      // Update local state
      holidays.value[dateKey] = text
    } catch (err) {
      console.error('Error saving event:', err)
      error.value = 'לא ניתן לשמור את האירוע'
      throw err
    } finally {
      saving.value = false
    }
  }

  /**
   * Delete an event
   * @param {string} dateKey - Date in YYYY-MM-DD format
   */
  async function deleteEvent(dateKey) {
    saving.value = true
    error.value = null

    try {
      const db = getFirebaseDb()
      await deleteDoc(doc(db, 'holiday', dateKey))

      // Update local state
      delete holidays.value[dateKey]
    } catch (err) {
      console.error('Error deleting event:', err)
      error.value = 'לא ניתן למחוק את האירוע'
      throw err
    } finally {
      saving.value = false
    }
  }

  /**
   * Navigate to previous month
   */
  function prevMonth() {
    if (currentMonth.value === 0) {
      currentMonth.value = 11
      currentYear.value--
    } else {
      currentMonth.value--
    }
  }

  /**
   * Navigate to next month
   */
  function nextMonth() {
    if (currentMonth.value === 11) {
      currentMonth.value = 0
      currentYear.value++
    } else {
      currentMonth.value++
    }
  }

  /**
   * Go to current month
   */
  function goToToday() {
    const now = new Date()
    currentMonth.value = now.getMonth()
    currentYear.value = now.getFullYear()
  }

  /**
   * Get event text for a specific date
   * @param {string} dateKey
   * @returns {string|null}
   */
  function getEvent(dateKey) {
    return holidays.value[dateKey] || null
  }

  /**
   * Check if a date is today
   * @param {Date} date
   * @returns {boolean}
   */
  function isToday(date) {
    return formatDateKey(date) === todayKey
  }

  return {
    // State
    holidays: readonly(holidays),
    loading,
    saving,
    error,
    currentMonth,
    currentYear,

    // Computed
    weeks,
    currentWeekDays,
    currentWeekEvents,
    hasWeekEvents,
    monthYearDisplay,

    // Actions
    loadHolidays,
    saveEvent,
    deleteEvent,
    prevMonth,
    nextMonth,
    goToToday,
    getEvent,
    isToday,

    // Utils
    formatDateKey,
    formatDateDisplay,

    // Constants
    DAY_NAMES,
    HEBREW_MONTHS,
  }
}
